import type React from 'react';
import { useParams } from 'react-router';
import Footer from '@/components/Footer';
import Loading from '@/components/UI/loading';
import Dashboard from '../../features/ApplicationForm/FormConfig/MKEERA/MkEERADashboard/Dashboard';
import { useMKEERAPublicData } from '../../hooks/ReactQueryHooks/useMKEERAQuery';

const PublicMKEERADashboard: React.FC = () => {
  // Get applicationId from route params
  const { applicationId } = useParams<{ applicationId: string }>();

  // Query to fetch MKEERA public data
  const { data, isLoading, error, refetch } = useMKEERAPublicData({
    params: {
      applicationId: applicationId!,
      perPage: 9999,
    }, // Fetch more records for comprehensive data
  });

  // Handle missing applicationId
  if (!applicationId) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-neutralColor-lightest'>
        <div className='bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4'>
          <div className='text-center'>
            <div className='text-red-500 text-4xl mb-4'>⚠️</div>
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>Invalid URL</h2>
            <p className='text-gray-600 mb-4'>Please access this dashboard through a valid URL with an application ID.</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-neutralColor-lightest'>
        <Loading loadingText='Loading MKEERA Dashboard...' />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-neutralColor-lightest'>
        <div className='bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4'>
          <div className='text-center'>
            <div className='text-red-500 text-4xl mb-4'>⚠️</div>
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>Unable to Load Dashboard</h2>
            <p className='text-gray-600 mb-4'>We're having trouble loading the MKEERA dashboard data. Please try again.</p>
            <button
              onClick={() => refetch()}
              className='bg-primaryColor-normal text-white px-4 py-2 rounded hover:bg-primaryColor-dark transition-colors'
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-neutralColor-lightest'>
      {/* Header */}
      <div className='bg-white shadow-sm border-b'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900'>Milwaukee Emergency Rental Assistance Dashboard</h1>
              <p className='text-gray-600 mt-2'>Real-time data and insights on rental assistance programs in Milwaukee</p>
            </div>
            <div className='text-right'>
              <div className='text-sm text-gray-500'>Total Records: {data?.meta?.count || '0'}</div>
              <div className='text-sm text-gray-500'>Last Updated: {new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {data?.data && data.data.length > 0 ? (
          <Dashboard data={data?.data || []} totalRecords={data?.meta?.count || 0} />
        ) : (
          <div className='bg-white p-8 rounded-lg shadow-lg text-center'>
            <div className='text-gray-400 text-4xl mb-4'>📊</div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>No Data Available</h3>
            <p className='text-gray-600'>There's currently no data available for the MKEERA dashboard.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PublicMKEERADashboard;
